/**
 * The two figure blocks a project shows on its Form and its Summary
 * (reference: "Estimated Cost Summary" and "Project Value Summary").
 *
 * Pure decimal arithmetic over the project's own lines, so the server presents
 * exactly what the form shows while it is being typed: the browser recomputes
 * on every keystroke, the server on every read, and both call this.
 */
import {
  ZERO_MONEY, addMoney, addRate, formatINR, lineAmount, pct, qty, rate, roundingAdjustment, splitTax, taxOn,
  type Money,
} from '../primitives/decimal.js'

/** The cells of a line these figures read. Rows arrive straight off the grid. */
export type FigureLine = Readonly<Record<string, unknown>>

export interface FigureRow {
  readonly key: string
  readonly label: string
  /** Decimal string. */
  readonly amount: string
  readonly emphasis?: boolean
}

const text = (v: unknown): string => (v === null || v === undefined ? '' : String(v).trim())

/** A cell as a decimal, or zero while it is blank or half-typed. */
const num = <T>(v: unknown, as: (x: string) => T, zero: T): T => {
  const s = text(v)
  if (s === '') return zero
  try { return as(s) } catch { return zero }
}

const q = (v: unknown) => num(v, qty, qty(0))
const r = (v: unknown) => num(v, rate, rate(0))

const sectionOf = (l: FigureLine): string => text(l['section']) || 'ITEM'

/** A BOQ line that carries a price — not a heading. */
export const isPricedBoqLine = (l: FigureLine): boolean =>
  sectionOf(l) === 'BOQ' && text(l['lineType']) !== 'SECTION'

/** No marks extra work outside the tender (docs/05 §5b). */
export const isNonTendered = (l: FigureLine): boolean => {
  const t = text(l['tendered']).toUpperCase()
  return t === 'NO' || t === 'FALSE'
}

/**
 * The two cost types that split a line between two buckets.
 *
 * A Material and Labour line is priced at a material rate (held in the supply
 * half, as on a two-rate BOQ line) plus a labour rate (the line's own rate).
 */
export const COST_BUCKETS = ['MATERIAL', 'LABOUR', 'BUNDLED', 'LUMPSUM'] as const
export type CostBucket = (typeof COST_BUCKETS)[number]

export interface EstimatedCost {
  readonly material: Money
  readonly labour: Money
  readonly bundled: Money
  readonly lumpsum: Money
  readonly total: Money
}

/**
 * What the estimate says the job will cost, by type.
 *
 * Every ESTIMATION line — those under a BOQ line and the common items and
 * overheads under none. Expense lines are lump sums by another name and are
 * counted with them.
 */
export const estimatedCost = (lines: readonly FigureLine[]): EstimatedCost => {
  const buckets: Record<CostBucket, Money[]> = { MATERIAL: [], LABOUR: [], BUNDLED: [], LUMPSUM: [] }
  for (const l of lines) {
    if (sectionOf(l) !== 'ESTIMATION') continue
    const type = text(l['costType']).toUpperCase() || 'MATERIAL'
    const quantity = q(l['quantity'])
    if (type === 'MATERIAL_AND_LABOUR') {
      buckets.MATERIAL.push(lineAmount(quantity, r(l['supplyRate'])))
      buckets.LABOUR.push(lineAmount(quantity, r(l['rate'])))
      continue
    }
    const amount = lineAmount(quantity, r(l['rate']))
    if (type === 'LABOUR') buckets.LABOUR.push(amount)
    else if (type === 'BUNDLED') buckets.BUNDLED.push(amount)
    else if (type === 'LUMPSUM' || type === 'EXPENSE') buckets.LUMPSUM.push(amount)
    else buckets.MATERIAL.push(amount)
  }
  const sum = (xs: Money[]): Money => (xs.length === 0 ? ZERO_MONEY : addMoney(...xs))
  const material = sum(buckets.MATERIAL)
  const labour = sum(buckets.LABOUR)
  const bundled = sum(buckets.BUNDLED)
  const lumpsum = sum(buckets.LUMPSUM)
  return { material, labour, bundled, lumpsum, total: addMoney(material, labour, bundled, lumpsum) }
}

export const estimatedCostRows = (lines: readonly FigureLine[]): FigureRow[] => {
  const c = estimatedCost(lines)
  return [
    { key: 'material', label: 'Material cost', amount: c.material },
    { key: 'labour', label: 'Labour cost', amount: c.labour },
    { key: 'bundled', label: 'Bundled cost', amount: c.bundled },
    { key: 'lumpsum', label: 'Lumpsum cost', amount: c.lumpsum },
    { key: 'total', label: 'Total estimated cost', amount: c.total, emphasis: true },
  ]
}

/** One BOQ line's price: supply plus installation, times a quantity. */
const boqValue = (l: FigureLine, quantity: unknown): Money =>
  lineAmount(q(quantity), addRate(r(l['rate']), r(l['supplyRate'])))

const isRateOnly = (l: FigureLine): boolean => {
  const v = text(l['rateOnly']).toUpperCase()
  return v === 'YES' || v === 'TRUE'
}

const pctLabel = (p: string): string => {
  const n = Number(p)
  return n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)
}

/**
 * The order value, its tax stated on its base, and what the job is worth as
 * designed.
 *
 * Tax is worked per line, half-up, then summed per rate — the same order of
 * rounding the document total uses — and split CGST + SGST or IGST by the
 * project's own Interstate answer. "Based on design" is the design quantity
 * (falling back to the ordered one) at the same rates; a rate-only line has no
 * quantity to design and is left out of it.
 */
export const projectValueRows = (
  lines: readonly FigureLine[], interstate: boolean,
): FigureRow[] => {
  const priced = lines.filter(isPricedBoqLine)
  const amounts: Money[] = []
  const nonTendered: Money[] = []
  const designed: Money[] = []
  const byRate = new Map<string, { base: Money[]; tax: Money[] }>()

  for (const l of priced) {
    const amount = boqValue(l, l['quantity'])
    amounts.push(amount)
    if (isNonTendered(l)) nonTendered.push(amount)
    if (!isRateOnly(l)) {
      const design = text(l['designQuantity']) === '' ? l['quantity'] : l['designQuantity']
      designed.push(boqValue(l, design))
    }
    const taxPct = num(l['taxPct'], pct, pct(0))
    if (Number(taxPct) === 0) continue
    const bucket = byRate.get(taxPct) ?? { base: [], tax: [] }
    bucket.base.push(amount)
    bucket.tax.push(taxOn(amount, taxPct))
    byRate.set(taxPct, bucket)
  }

  const sum = (xs: Money[]): Money => (xs.length === 0 ? ZERO_MONEY : addMoney(...xs))
  const subtotal = sum(amounts)
  const out: FigureRow[] = [{ key: 'subtotal', label: 'Order value - Sub total', amount: subtotal }]

  const taxes: Money[] = []
  for (const [taxPct, bucket] of [...byRate.entries()].sort((a, b) => Number(a[0]) - Number(b[0]))) {
    const base = sum(bucket.base)
    const tax = sum(bucket.tax)
    taxes.push(tax)
    const split = splitTax(tax, interstate)
    const on = `on ₹${formatINR(base, { symbol: false })}`
    if (interstate) {
      out.push({ key: `igst:${taxPct}`, label: `IGST @ ${pctLabel(taxPct)}% ${on}`, amount: split.igst })
    } else {
      const half = pctLabel(String(Number(taxPct) / 2))
      // SGST before CGST, as the reference lists them.
      out.push({ key: `sgst:${taxPct}`, label: `SGST @ ${half}% ${on}`, amount: split.sgst })
      out.push({ key: `cgst:${taxPct}`, label: `CGST @ ${half}% ${on}`, amount: split.cgst })
    }
  }

  const { rounded, adjustment } = roundingAdjustment(addMoney(subtotal, sum(taxes)))
  if (Number(adjustment) !== 0) out.push({ key: 'rounding', label: 'Rounding adjustment', amount: adjustment })
  out.push({ key: 'total', label: 'Order value - Total', amount: rounded, emphasis: true })
  if (nonTendered.length > 0) {
    out.push({ key: 'nonTendered', label: 'Of which non-tendered (NT-BOQ)', amount: sum(nonTendered) })
  }
  out.push({ key: 'design', label: 'Project value (based on design)', amount: sum(designed) })
  return out
}
